import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Observer } from 'mobx-react';
import { Button } from '@shared/components/Button';
import { Box, Paper, useTheme, DialogTitle, Typography, Tooltip, Chip, Divider, Stack } from '@mui/material';
import { components as RSComponents } from 'react-select';
import { messages } from '../../procedures.messages';
import { toast } from 'react-toastify';
import { ToastMessage } from '@shared/components/Toast';
import { useProceduresUIStore, useProcedureUIStore, useUserPermissionsUIStore } from '@core/useStores';
import { ThemeProps } from '@styles/theme';
import { useStyles } from './VersionAssignmentModal.styles';
import { IStoresEnv } from '@core/storesEnv';
import { getEnv } from 'mobx-state-tree';
import { IProcedureVersionStore, ProcedureVersionStore } from 'Procedures/stores/Procedure.store';
import { ArrayDropdown } from '@shared/components/SelectDropdown/ArrayDropdown';
import { Modal } from '@shared/components/Modal';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import _ from 'lodash';
import { ISelectOption } from '@shared/helpers/form/ISelectOption';
import { DeviceIcon } from '@shared/components/DeviceIcon';
import { IProcedureDevice } from 'Procedures/stores';
import { ExpandButton } from '@shared/components/Button/ExpandButton';
import { COMMON_SELECT_PROPS } from '../ProcedureModal/components/ProcedureDetailsForm';
import { ProcedureVersionAssignment } from 'Procedures/services';
import FallbackProcedureIcon from '@assets/fallback-procedure-icon.png';
import { ExecutionModes } from '@shared/components/ExecutionModes';
import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';

type VersionRowData = {
  version: IProcedureVersionStore;
  users: ISelectOption[];
  groups: ISelectOption[];
};

interface VersionAssignmentModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  selectedProcedureId: string;
}


interface UserOptionWithDetails {
  value: string;
  label: string;
  email?: string;
  groupIds?: string[];
}

interface VersionRowProps {
  version: IProcedureVersionStore;
  users: ISelectOption[];
  groups: ISelectOption[];
  allUsers: UserOptionWithDetails[];
  allGroups: ISelectOption[];
  canChangeUsers?: boolean;
  canChangeGroups?: boolean;
  onUsersChange?: (values: ReadonlyArray<object>, fieldId: string) => void;
  onGroupsChange?: (values: ReadonlyArray<object>, fieldId: string) => void;
}

interface DeviceType {
  type: string | null;
  platform: string | null;
  environments: string | null;
}

// ── Avatar helpers ─────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
    '#7B5EA7', '#4A90D9', '#50AE8C', '#D9804A', '#D94F5A',
    '#3DAB9C', '#8B45B8', '#3D7EC4', '#C06A4A', '#5D9E57',
];

function getInitials(name: string): string {
    return name.trim().split(/\s+/).slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('');
}

function getAvatarColor(id: string): string {
    let h = 0;
    for (let i = 0; i < id.length; i++) {h = id.charCodeAt(i) + ((h << 5) - h);}

    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

const UserAvatarMultiValue = (props: any) => {
    if (!props.data?.label) {return <></>;}
    const initials = getInitials(props.data.label);
    const bg = getAvatarColor(props.data.value);
    const email = props.data.email;

    return (
        <Tooltip title={email || props.data.label} arrow>
            <span>
                <RSComponents.MultiValue {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', pl: '1px' }}>
                        <Box sx={{
                            width: 15, height: 15, borderRadius: '50%',
                            backgroundColor: bg, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: 7, fontWeight: 800, color: 'white', flexShrink: 0, lineHeight: 1,
                            letterSpacing: '-0.5px',
                        }}>
                            {initials}
                        </Box>
                        <span style={{ fontSize: 13 }}>{props.data.label}</span>
                    </Box>
                </RSComponents.MultiValue>
            </span>
        </Tooltip>
    );
};

const UserAvatarOption = (props: any) => {
    if (!props.data?.label) {return <RSComponents.Option {...props} />;}
    const initials = getInitials(props.data.label);
    const bg = getAvatarColor(props.data.value);
    const email = props.data.email;

    return (
        <RSComponents.Option {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                    width: 26, height: 26, borderRadius: '50%',
                    backgroundColor: bg, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 700, color: 'white', flexShrink: 0,
                }}>
                    {initials}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{props.data.label}</span>
                    {email && (
                        <span style={{ fontSize: 11, opacity: 0.58, letterSpacing: '0.01em' }}>{email}</span>
                    )}
                </Box>
            </Box>
        </RSComponents.Option>
    );
};

const UserMenuList = (props: any) => (
    <RSComponents.MenuList {...props}>
        {props.children}
        <div style={{ height: 8 }} />
    </RSComponents.MenuList>
);

const USER_AVATAR_COMPONENTS = {
    MultiValue: UserAvatarMultiValue,
    Option: UserAvatarOption,
    MenuList: UserMenuList,
};

const VersionRow: React.FC<VersionRowProps> = ({
    version, users, groups, allUsers, allGroups,
    canChangeUsers, canChangeGroups, onUsersChange, onGroupsChange,
}) => {
    const theme = useTheme<ThemeProps>();
    const [expanded, setExpanded] = useState(false);
    const useStores = getEnv<IStoresEnv>(useProceduresUIStore());
    const MAX_LENGTH = 120;
    const getLanguage = new Intl.DisplayNames(['EN'], { type: 'language' });

    const devices: DeviceType[] = version.devices.map((d: IProcedureDevice) => ({
        type: d.device,
        platform: d.platform,
        environments: d.environments.join('|'),
    }));
    const uniqueDevices = _.uniqBy(devices, d => `${d.platform}-${d.environments}`);

    const truncatedDescription = !version.description
        ? ''
        : version.description.length > MAX_LENGTH && !expanded
            ? version.description.substring(0, MAX_LENGTH) + '...'
            : version.description;

    const isLatest = version.id === 'latest';

    return (
        <Paper
            elevation={0}
            sx={{
                border: `1.5px solid`,
                borderColor: isLatest ? 'primary.main' : theme.colors.grey_light,
                borderRadius: 2,
                mb: 1.5,
                overflow: 'hidden',
                backgroundColor: theme.colors.white,
                transition: 'box-shadow 0.18s ease',
                '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.07)' },
            }}
        >
            {/* ── Version info header ─────────────────────────────────────── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75,
                px: 1.5, py: 1,
                background: isLatest
                    ? `linear-gradient(90deg, ${theme.colors.transp_violet} 0%, transparent 100%)`
                    : 'transparent',
                borderBottom: `1px solid ${theme.colors.grey_light}`,
            }}>
                {/* Version badge */}
                <Box sx={{
                    display: 'inline-flex', alignItems: 'center',
                    border: `1px solid ${isLatest ? 'primary.main' : theme.colors.grey_light}`,
                    borderColor: isLatest ? 'primary.main' : theme.colors.grey_light,
                    borderRadius: 1.5, px: 1, py: 0.25,
                    bgcolor: isLatest ? theme.colors.transp_violet : 'transparent', flexShrink: 0,
                }}>
                    <Typography variant="subtitle2" color={isLatest ? 'primary' : 'textPrimary'}
                        sx={{ fontWeight: 700, fontSize: 15, minWidth: 24, padding: '0 6px' }}>
                        {version.version}
                    </Typography>
                    {uniqueDevices.length > 0 && (
                        <>
                            <Divider orientation="vertical" flexItem variant="middle" sx={{ mx: 0.75 }} />
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                                {uniqueDevices.map((device, i) => (
                                    <DeviceIcon key={i} platform={device.platform ?? ''} environments={device.environments} size="small" id={i} />
                                ))}
                            </Box>
                        </>
                    )}
                    {version.executionModes?.length > 0 && (
                        <>
                            <Divider orientation="vertical" flexItem variant="middle" sx={{ mx: 0.75 }} />
                            <ExecutionModes modes={version.executionModes} size="small" />
                        </>
                    )}
                </Box>

                {/* Date */}
                {!isLatest && (
                    <Tooltip title={new Date(version.date).toLocaleString()} arrow>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: 12, padding: '0 4px' }}>
                            {new Date(version.date).toLocaleDateString()}
                        </Typography>
                    </Tooltip>
                )}

                {/* Editor version */}
                {version.editorVersion && (
                    <Tooltip title={messages['versionAssignmentModal.editorVersion.tooltip']} arrow>
                        <Chip label={version.editorVersion} color="primary" variant="outlined" size="small"
                            sx={{ height: 18, fontSize: 12, padding: '0 4px', marginRight: 0.5, '& .MuiChip-label': { px: 0.75 } }} />
                    </Tooltip>
                )}

                {/* Languages */}
                {version.languages?.length > 0 && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title={messages['versionAssignmentModal.languages.tooltip']} arrow>
                            <LanguageIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Tooltip>
                        {version.languages.map((lang: string, i: number) => (
                            <Tooltip key={i} title={getLanguage.of(lang)} arrow>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: 11 }}>
                                    {lang}{i < version.languages.length - 1 ? ' · ' : ''}
                                </Typography>
                            </Tooltip>
                        ))}
                    </Stack>
                )}

                <Box flex={1} />

                {/* Uploader — wrapped in Observer for reactivity */}
                {!isLatest && version.uploadedBy && (
                    <Observer>
                        {() => {
                            const uploaderLabel = useStores.filtersOptions.getUsersOptions.find(u => u.value === version.uploadedBy)?.label;

                            return uploaderLabel ? (
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: 11.5 }}>
                                    {messages['versionAssignmentModal.uploadedBy']}: <strong>{uploaderLabel}</strong>
                                </Typography>
                            ) : null;
                        }}
                    </Observer>
                )}
            </Box>

            {/* ── Description ─────────────────────────────────────────────── */}
            {version.description && (
                <Box sx={{ px: 1.5, pt: 0.75, display: 'flex', alignItems: 'flex-start', gap: 0.5, margin: '4px 0' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: 12, lineHeight: 1.4 }}>
                        {truncatedDescription}
                    </Typography>
                    {version.description.length > MAX_LENGTH && (
                        <Tooltip title={messages['versionAssignmentModal.expandDescription.tooltip']} arrow>
                            <ExpandButton expand={expanded} onClick={() => setExpanded(!expanded)}
                                aria-expanded={expanded} aria-label="show more"
                                sx={{ p: 0, width: 18, height: 18, ml: 0.25 }}>
                                <ExpandMoreIcon sx={{ fontSize: 13 }} />
                            </ExpandButton>
                        </Tooltip>
                    )}
                </Box>
            )}

            {/* ── Users + Groups assignment ────────────────────────────────── */}
            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Users row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.75, minWidth: 62, flexShrink: 0,
                        alignSelf: 'center', marginRight: 2.5 }}>
                        <PersonIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        <Typography variant="caption" color="primary" sx={{ fontSize: 12, fontWeight: 600 }}>
              Users
                        </Typography>
                    </Box>
                    <Box flex={1}>
                        <ArrayDropdown
                            fieldId={version.id}
                            values={users}
                            options={allUsers}
                            onChange={onUsersChange}
                            menuPortalTarget={document.body}
                            placeholder={messages['versionAssignmentModal.dropdown.noUsersAssigned']}
                            disabled={!canChangeUsers}
                            {...COMMON_SELECT_PROPS}
                            components={{ ...COMMON_SELECT_PROPS.components, ...USER_AVATAR_COMPONENTS }}
                        />
                    </Box>
                </Box>

                {/* Groups row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.75, minWidth: 62, flexShrink: 0,
                        alignSelf: 'center', marginRight: 2.5 }}>
                        <GroupsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: 12, fontWeight: 600 }}>
              Groups
                        </Typography>
                    </Box>
                    <Box flex={1}>
                        <ArrayDropdown
                            fieldId={version.id}
                            values={groups}
                            options={allGroups}
                            onChange={onGroupsChange}
                            menuPortalTarget={document.body}
                            placeholder={messages['versionAssignmentModal.dropdown.noGroupsAssigned']}
                            disabled={!canChangeGroups}
                            {...COMMON_SELECT_PROPS}
                        />
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

export interface VersionAssignmentPanelHandle {
  save: (silent?: boolean) => Promise<void>;
}

export const VersionAssignmentPanel = React.forwardRef<VersionAssignmentPanelHandle, {
  selectedProcedureId: string;
  onSaved?: () => void;
  onCancel?: () => void;
  hideSaveButton?: boolean;
      }>((
          { selectedProcedureId: procedureId, onSaved, onCancel, hideSaveButton },
          ref
      ) => {


          // ── All hooks declared unconditionally ──────────────────────────────────────
          const handleSaveRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});
          useImperativeHandle(ref, () => ({ save: (silent?: boolean) => handleSaveRef.current(silent) }));

          const proceduresUIStore = useProceduresUIStore();
          const procedureUIStore = useProcedureUIStore();
          const userPermissionsUIStore = useUserPermissionsUIStore();

          const { filtersOptions } = getEnv<IStoresEnv>(proceduresUIStore);
          const allGroups = filtersOptions.getGroupsOptions;
          const allUsers = filtersOptions.getUsersOptionsWithDetails;

          const procedure = proceduresUIStore.procedures.find(p => p.id === procedureId);
          const users = procedure?.users ?? [];
          const groups = procedure?.groups ?? [];

          const [isSubmitting, setIsSubmitting] = useState(false);
          const [versionsMap, setVersionsMap] = useState<Map<string, VersionRowData>>(new Map());

          // Load filter options on mount so uploader names & dropdown options are available
          useEffect(() => {
              filtersOptions.loadFilterOptions();
          }, []);

          // Initialize versionsMap whenever source data changes
          useEffect(() => {
              if (!procedure || !procedure.versions.length) {return;}

              const orderedVersions = _.orderBy(procedure.versions, ['date'], ['desc']);
              const latestVer: IProcedureVersionStore = ProcedureVersionStore.create({
                  id: 'latest',
                  version: 'Latest',
                  date: orderedVersions[0].date,
                  description: null,
                  executionModes: _.uniqBy(orderedVersions.flatMap(v => v.executionModes), m => m),
                  environments: _.uniqBy(orderedVersions.flatMap(v => v.environments), e => e),
                  devices: _.uniqBy(orderedVersions.flatMap(v => v.getRawDevices), d => (d as any).platform),
              });
              const verMap = new Map(orderedVersions.map(v => [v.id, v]));
              const vMap = new Map<string, VersionRowData>();

              // Primary: grid store users/groups (has data.assignedVersion info)
              // Fallback: procedureGeneralInfo (all users go to 'latest')
              const generalUsers = procedureUIStore.procedureGeneralInfo?.users ?? [];
              const generalGroups = procedureUIStore.procedureGeneralInfo?.groups ?? [];

              const sourceUsers = users.length > 0
                  ? users
                  : generalUsers.map(u => ({ id: u.id, name: u.name, data: null as any }));
              const sourceGroups = groups.length > 0
                  ? groups
                  : generalGroups.map(g => ({ id: g.id, name: g.name, data: null as any }));

              sourceUsers.forEach(user => {
                  const actualUser = allUsers.find(u => u.value === user.id);
                  const userToAdd = actualUser || { value: user.id, label: user.name };
                  const versionId = user.data?.assignedVersion ?? 'latest';
                  const entry = vMap.get(versionId);
                  if (entry) {
                      entry.users.push(userToAdd);
                  } else {
                      vMap.set(versionId, {
                          version: versionId === 'latest' ? latestVer : (verMap.get(versionId) ?? latestVer),
                          users: [userToAdd],
                          groups: [],
                      });
                  }
              });

              sourceGroups.forEach(group => {
                  const actualGroup = allGroups.find(g => g.value === group.id);
                  const groupToAdd = actualGroup || { value: group.id, label: group.name };
                  const versionId = group.data?.assignedVersion ?? 'latest';
                  const entry = vMap.get(versionId);
                  if (entry) {
                      entry.groups.push(groupToAdd);
                  } else {
                      vMap.set(versionId, {
                          version: versionId === 'latest' ? latestVer : (verMap.get(versionId) ?? latestVer),
                          users: [],
                          groups: [groupToAdd],
                      });
                  }
              });

              setVersionsMap(vMap);
              // Re-run when counts change (handles async load of procedure data & filter options)
          }, [
              users.length,
              groups.length,
              allUsers.length,
              allGroups.length,
              procedureUIStore.procedureGeneralInfo,
          ]);

          // ── Conditional render guard — after ALL hooks ───────────────────────────────
          if (!procedure || !procedure.versions.length) {return null;}

          const versions = _.orderBy(procedure.versions, ['date'], ['desc']);
          const latestVersion: IProcedureVersionStore = ProcedureVersionStore.create({
              id: 'latest',
              version: 'Latest',
              date: versions[0].date,
              description: null,
              executionModes: _.uniqBy(versions.flatMap(v => v.executionModes), mode => mode),
              environments: _.uniqBy(versions.flatMap(v => v.environments), env => env),
              devices: _.uniqBy(versions.flatMap(v => v.getRawDevices), device => (device as any).platform),
          });
          const versionMap = new Map(versions.map(version => [version.id, version]));

          const handleAssignmentChanged = (isUser: boolean, values: ReadonlyArray<object>, fieldId: string) => {
              const version = versionsMap.get(fieldId);
              if (!version) {
                  const newVersion = versionMap.get(fieldId) ?? latestVersion;
                  const newValues = values as ISelectOption[];
                  const newMap = new Map(versionsMap);
                  newMap.set(fieldId, {
                      version: newVersion,
                      users: isUser ? newValues : [],
                      groups: isUser ? [] : newValues,
                  });
                  setVersionsMap(newMap);

                  return;
              }

              const newValues = values as ISelectOption[];
              if (isUser) { version.users = newValues; } else { version.groups = newValues; }
              setVersionsMap(new Map(versionsMap));
          };

          const handleSave = async (silent?: boolean) => {
              setIsSubmitting(true);

              try {
                  const assignments: Array<ProcedureVersionAssignment> = [];
                  const alreadyAssignedUsers: Array<ISelectOption> = [];
                  const alreadyAssignedGroups: Array<ISelectOption> = [];

                  const latestVersionData = versionsMap.get('latest');
                  if (latestVersionData) {
                      assignments.push({
                          id: procedureId,
                          versionId: null,
                          userIds: latestVersionData.users?.map(user => user.value),
                          groupIds: latestVersionData.groups?.map(group => group.value),
                      });
                      alreadyAssignedUsers.push(...latestVersionData.users);
                      alreadyAssignedGroups.push(...latestVersionData.groups);
                  }

                  versionsMap.forEach((versionData, versionId) => {
                      if (versionId === 'latest') {return;}
                      const version = versionMap.get(versionId);
                      if (!version) {return;}

                      const filteredUsers = versionData.users.filter(u => !alreadyAssignedUsers.find(a => a.value === u.value));
                      const filteredGroups = versionData.groups.filter(g => !alreadyAssignedGroups.find(a => a.value === g.value));

                      assignments.push({
                          id: procedureId,
                          versionId: versionId,
                          userIds: filteredUsers.map(u => u.value),
                          groupIds: filteredGroups.map(g => g.value),
                      });

                      alreadyAssignedUsers.push(...filteredUsers);
                      alreadyAssignedGroups.push(...filteredGroups);
                  });

                  const result = await procedureUIStore.saveVersionAssignments(assignments);

                  if (result.success) {
                      proceduresUIStore.load();
                      if (!silent) {
                          toast.success(<ToastMessage message={messages['versionAssignmentModal.success']} type='success' />);
                      }
                      onSaved?.();
                  } else {
                      toast.error(<ToastMessage message={messages['versionAssignmentModal.error']} type='error' />);
                  }
              } catch {
                  toast.error(<ToastMessage message={messages['versionAssignmentModal.error']} type='error' />);
              } finally {
                  setIsSubmitting(false);
              }
          };

          handleSaveRef.current = handleSave;

          return (
              <Box sx={{ width: '100%', p: 2 }}>
                  <VersionRow
                      key={latestVersion.id}
                      version={latestVersion}
                      users={versionsMap.get('latest')?.users ?? []}
                      groups={versionsMap.get('latest')?.groups ?? []}
                      allUsers={allUsers}
                      allGroups={allGroups}
                      canChangeUsers={userPermissionsUIStore.canChangeAssignedUsers}
                      canChangeGroups={userPermissionsUIStore.canChangeAssignedGroups}
                      onUsersChange={(values, fieldId) => handleAssignmentChanged(true, values, fieldId)}
                      onGroupsChange={(values, fieldId) => handleAssignmentChanged(false, values, fieldId)}
                  />
                  {versions.map(version => (
                      <VersionRow
                          key={version.id}
                          version={version}
                          users={versionsMap.get(version.id)?.users ?? []}
                          groups={versionsMap.get(version.id)?.groups ?? []}
                          allUsers={allUsers}
                          allGroups={allGroups}
                          canChangeUsers={userPermissionsUIStore.canChangeAssignedUsers}
                          canChangeGroups={userPermissionsUIStore.canChangeAssignedGroups}
                          onUsersChange={(values, fieldId) => handleAssignmentChanged(true, values, fieldId)}
                          onGroupsChange={(values, fieldId) => handleAssignmentChanged(false, values, fieldId)}
                      />
                  ))}

                  {!hideSaveButton && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                          {onCancel && (
                              <Button onClick={onCancel} color="secondary" sx={{ mr: 2 }}>
                                  {messages['versionAssignmentModal.cancel']}
                              </Button>
                          )}
                          <Button onClick={handleSave} color="primary" disabled={isSubmitting}>
                              {messages['versionAssignmentModal.save']}
                          </Button>
                      </Box>
                  )}
              </Box>
          );
      });

VersionAssignmentPanel.displayName = 'VersionAssignmentPanel';

export const VersionAssignmentModal: React.FC<VersionAssignmentModalProps> = ({
    isOpen,
    onRequestClose,
    selectedProcedureId: procedureId,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const proceduresUIStore = useProceduresUIStore();
    const procedure = proceduresUIStore.procedures.find(p => p.id === procedureId);

    if (!procedure) {return null;}

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
        >
            <DialogTitle sx={{ zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {procedure.procedurePreview ? (
                        <img
                            className={styles.procedureImage}
                            src={procedure.procedurePreview.src}
                            alt={procedure.procedurePreview.description ?? ''}
                        />
                    ) : (
                        <img className={styles.procedureImage} src={FallbackProcedureIcon} />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2, alignItems: 'flex-start', flexGrow: 1, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h5" color='primary'>{procedure.name}</Typography>
                            {procedure.type && (
                                <Paper elevation={2} sx={{ pt: 1, pl: 2, pr: 2, mt: 1, mb: 1, ml: 4, fontSize: 16 }}>{procedure.type}</Paper>
                            )}
                        </Box>
                        <Typography variant="body2" color='textSecondary'>{procedure.description}</Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <VersionAssignmentPanel
                selectedProcedureId={procedureId}
                onSaved={onRequestClose}
                onCancel={onRequestClose}
            />
        </Modal>
    );
};